import { all, call, put, takeLatest } from 'redux-saga/effects';
import { push } from 'react-router-redux';
import queryString from 'query-string';

import { STORAGE } from '@constants';

import { setInstantSurveyAuthToken } from '@api/instant_survey/api';
import { setChatAuthToken } from '@api/narrative-projects/api';
import { readApiServiceToken, readChatToken } from '@api/tokens';
import { readAppContext as readAppContextApi } from '@api/app_context';
import {
  readUser as readUserApi,
  switchToBaseUser as switchToBaseUserApi,
  switchToProxyUser as switchToProxyUserApi,
  updateEmailPreferences,
  updateProfile,
  updateSurveySharingPreference as updateSurveySharingPreferenceApi,
} from '@api/user';
import { readClientSubscription as readClientSubscriptionApi } from '@api/client';
import { getWebsocketEndpoint } from '@api/websocket';
import { login as userLogin, logout as userLogout } from '@api/auth';

import { websocketConnect, websocketDisconnect, websocketReset } from '../../slices/websocket';

import {
  loginFailed,
  login as loginRequested,
  loginSucceeded,
  logoutFailed,
  logout as logoutRequested,
  logoutSucceeded,
  switchToBaseUser,
  switchToProxyUser,
  switchUserFailed,
  switchUserSucceeded,
  updatePersona,
  updatePersonaFailed,
  updatePersonaSucceeded,
  updateSurveySharingPreference,
  updateSurveySharingPreferenceFailed,
  updateSurveySharingPreferenceSucceeded,
  userFetch,
  userFetchFailed,
  userFetchSucceeded,
  userUpdateEmailPreferencesFailed,
  userUpdateEmailPreferencesRequested,
  userUpdateEmailPreferencesSucceeded,
} from '../../slices/user';

const hasNarrativesChatAccess = (permissions = {}) =>
  Boolean(permissions?.canAccessBuildYourOwn) || Boolean(permissions?.canAccessChat);

export function* login(action) {
  const { data, navigate, next } = action.payload;

  try {
    yield call(userLogin, data);
    yield call(navigate, next);
    yield put(loginSucceeded());
  } catch (error) {
    yield put(loginFailed({ error }));
  }
}

export function* logout(action) {
  const { isAuthenticated = true, location, navigate } = action.payload;

  try {
    yield call(setChatAuthToken, null);
    if (isAuthenticated) {
      yield call(userLogout);
    }
    yield put(websocketDisconnect());
    yield put(websocketReset());
    yield put(logoutSucceeded());
  } catch (error) {
    yield put(logoutFailed({ error }));
  } finally {
    yield call(navigate, {
      pathname: '/login',
      replace: true,
      search: queryString.stringify({
        next: location?.search ? location?.pathname + location?.search : location?.pathname,
      }),
    });
  }
}

export function* readUser() {
  try {
    const [userResponse, appContextResponse, jwtToken] = yield all([
      call(readUserApi),
      call(readAppContextApi),
      call(readApiServiceToken),
    ]);

    let subscriptions = [];
    if (
      userResponse?.data?.permissions?.canAccessSurveySubscription === true ||
      userResponse?.data?.permissions?.canEditSurveySubscription === true
    ) {
      subscriptions = yield call(readClientSubscriptionApi);
    }

    const payload = {
      ...userResponse.data,
      ...appContextResponse.data,
      clientInfo: {
        ...(userResponse.data?.clientInfo || {}),
        subscriptionDetails: subscriptions || [],
      },
    };

    yield put(websocketReset());
    yield put(
      websocketConnect(
        getWebsocketEndpoint({
          clientId: payload?.clientInfo?.id,
          userId: payload?.id,
        })
      )
    );

    setInstantSurveyAuthToken(jwtToken);
    yield call(setChatAuthToken, null);
    yield put(userFetchSucceeded(payload));

    if (hasNarrativesChatAccess(payload?.permissions)) {
      try {
        const chatToken = yield call(readChatToken);
        yield call(setChatAuthToken, chatToken);
      } catch (chatTokenError) {
        console.error('Failed to initialize chat auth token during user bootstrap', chatTokenError);
      }
    }
  } catch (error) {
    yield put(userFetchFailed(error));
    if (error?.response && error?.response?.status === 401) {
      yield put(push('/login'));
    }
  }
}

export function* switchUserToBase({ payload: { baseAccountId, navigate } }) {
  try {
    localStorage.removeItem(STORAGE.filters);
    yield call(setChatAuthToken, null);
    yield call(switchToBaseUserApi, { baseAccountId });
    yield call(navigate, '/dashboard/user/info');
    yield put(switchUserSucceeded());
    yield put(userFetch());
  } catch (error) {
    yield put(switchUserFailed(error));
    yield put(userFetch());
  }
}

export function* switchUserToProxy({ payload: { navigate, proxyAccountId } }) {
  try {
    localStorage.removeItem(STORAGE.filters);
    yield call(setChatAuthToken, null);
    yield call(switchToProxyUserApi, { proxyAccountId });
    yield call(navigate, '/');
    yield put(switchUserSucceeded());
  } catch (error) {
    yield put(switchUserFailed(error));
    yield put(userFetch());
  }
}

export function* updateUserEmailPreferences({ payload: { emailOptions, userId } }) {
  try {
    const response = yield call(updateEmailPreferences, { emailOptions, userId });
    yield put(userUpdateEmailPreferencesSucceeded(response.data));
  } catch (error) {
    yield put(userUpdateEmailPreferencesFailed(error));
  }
}

export function* updateUserPersona({ payload: { persona } }) {
  try {
    const response = yield call(updateProfile, { profile: persona });

    yield put(updatePersonaSucceeded(response.data));
  } catch (error) {
    yield put(updatePersonaFailed(error));
  }
}

export function* updateUserSurveySharingPreference({ payload: { surveySharingAutomatically } }) {
  try {
    const response = yield call(updateSurveySharingPreferenceApi, {
      surveySharingAutomatically,
    });

    yield put(updateSurveySharingPreferenceSucceeded(response.data));
  } catch (error) {
    yield put(updateSurveySharingPreferenceFailed(error));
  }
}

export function* userSagas() {
  yield all([
    watchForLogin(),
    watchForLogout(),
    watchForReadUser(),
    watchForSwitchToBaseUser(),
    watchForSwitchToProxyUser(),
    watchForUpdatePersona(),
    watchForUpdateSurveySharingPreference(),
    watchForUpdateUserEmailPreferences(),
  ]);
}

export function* watchForLogin() {
  yield takeLatest(loginRequested.type, login);
}

export function* watchForLogout() {
  yield takeLatest(logoutRequested.type, logout);
}

export function* watchForReadUser() {
  yield takeLatest(userFetch.type, readUser);
}

export function* watchForSwitchToBaseUser() {
  yield takeLatest(switchToBaseUser.type, switchUserToBase);
}

export function* watchForSwitchToProxyUser() {
  yield takeLatest(switchToProxyUser.type, switchUserToProxy);
}

export function* watchForUpdatePersona() {
  yield takeLatest(updatePersona.type, updateUserPersona);
}

export function* watchForUpdateSurveySharingPreference() {
  yield takeLatest(updateSurveySharingPreference.type, updateUserSurveySharingPreference);
}

export function* watchForUpdateUserEmailPreferences() {
  yield takeLatest(userUpdateEmailPreferencesRequested.type, updateUserEmailPreferences);
}
